import React, { useLayoutEffect, useRef, useState } from "react";

export type FramePreset = "desktop" | "tablet" | "phone";

interface FrameDims {
  width: number;
  height: number;
  /** Simulated viewport width used to decide if mobile fullscreen applies */
  simulatedViewportWidth: number;
}

const SIMULATE_CSS = `
@keyframes vel-sim-slideUp { 0%{transform:translateY(20px);opacity:0} 100%{transform:translateY(0);opacity:1} }
@keyframes vel-sim-fade { 0%{opacity:0} 100%{opacity:1} }
@keyframes vel-sim-pop { 0%{transform:scale(.85);opacity:0} 60%{transform:scale(1.04);opacity:1} 100%{transform:scale(1)} }
@keyframes vel-sim-pulse { 0%,100%{box-shadow:0 0 0 0 rgba(22,112,231,.5)} 50%{box-shadow:0 0 0 14px rgba(22,112,231,0)} }
@keyframes vel-sim-shadowFlash { 0%,100%{filter:drop-shadow(0 4px 8px rgba(0,0,0,.15))} 50%{filter:drop-shadow(0 12px 32px rgba(0,0,0,.45))} }
@keyframes vel-sim-glass { 0%,100%{backdrop-filter:blur(0px);background:rgba(255,255,255,0)} 50%{backdrop-filter:blur(8px);background:rgba(255,255,255,.4)} }
@keyframes vel-sim-flash { 0%,100%{outline:0 solid transparent} 50%{outline:3px solid var(--color-primary, #1670E7);outline-offset:-3px} }
@keyframes vel-sim-shake { 10%,90%{transform:translateX(-2px)} 20%,80%{transform:translateX(3px)} 30%,50%,70%{transform:translateX(-5px)} 40%,60%{transform:translateX(5px)} }
@keyframes vel-sim-bounce { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-12px)} }
@keyframes vel-sim-ring { 0%{box-shadow:0 0 0 0 rgba(22,112,231,.7)} 100%{box-shadow:0 0 0 24px rgba(22,112,231,0)} }
@keyframes vel-sim-typing { 0%,100%{transform:translateY(0);opacity:.6} 50%{transform:translateY(-3px);opacity:1} }

.vel-sim-openAnimation { animation: vel-sim-slideUp .6s ease-out 1; }
.vel-sim-bubbleEntrance { animation: vel-sim-pop .5s ease-out 2; }
.vel-sim-launcherPulse { animation: vel-sim-pulse 1.4s ease-out 2; }
.vel-sim-shadowFlash { animation: vel-sim-shadowFlash 1.2s ease-in-out 2; }
.vel-sim-glassmorphism { animation: vel-sim-glass 1.5s ease-in-out 2; }
.vel-sim-messageReceivedFlash { animation: vel-sim-flash .25s ease-out 3; }
.vel-sim-typingIndicator-dots,
.vel-sim-typingIndicator-wave,
.vel-sim-typingIndicator-thinking,
.vel-sim-typingIndicator-none { animation: vel-sim-typing .8s ease-in-out 3; }
.vel-sim-launcherAttention-shake { animation: vel-sim-shake .6s ease-in-out 3; }
.vel-sim-launcherAttention-bounce { animation: vel-sim-bounce .6s ease-in-out 3; }
.vel-sim-launcherAttention-ring { animation: vel-sim-ring 1.2s ease-out 2; border-radius: inherit; }
@media (prefers-reduced-motion: reduce) {
  [class*="vel-sim-"] { animation: none !important; }
}
`;

export const FRAME_PRESETS: Record<FramePreset, FrameDims & { label: string }> = {
  desktop: { width: 420, height: 680, label: "Desktop", simulatedViewportWidth: 1280 },
  tablet:  { width: 400, height: 620, label: "Tablet",  simulatedViewportWidth: 768  },
  phone:   { width: 320, height: 560, label: "Phone",   simulatedViewportWidth: 390  },
};

interface Props {
  children: React.ReactNode;
  /** actual configured widget width, for scaling/caption */
  actualWidth?: number;
  /** actual configured widget height, for scaling/caption */
  actualHeight?: number;
  /** "Left" | "Right" — controls transform-origin corner */
  launcherPosition?: "Left" | "Right";
  /** "top" | "middle" | "bottom" vertical anchor */
  launcherVerticalPosition?: "top" | "middle" | "bottom";
  /** horizontal offset from the edge (px) — configured sideSpacing/launcherOffsetX */
  sideSpacing?: number;
  /** vertical offset from the chosen vertical anchor (px) */
  bottomSpacing?: number;
  /** Configured window border radius (px). Applied to widget host for corner masking. */
  windowBorderRadius?: number;
  /** When true, simulate mobileFullscreen on presets whose simulated viewport is below the breakpoint. */
  mobileFullscreen?: boolean;
  /** The configured mobile breakpoint (px) — used to decide if a preset triggers fullscreen. Defaults to 640. */
  mobileBreakpointPx?: number;
  /** When true, show a loading overlay while the widget script initialises. */
  isLoading?: boolean;
  /** default preset */
  defaultPreset?: FramePreset;
}

export default function PreviewDeviceFrame({
  children,
  actualWidth,
  actualHeight,
  launcherPosition = "Right",
  launcherVerticalPosition = "bottom",
  sideSpacing,
  bottomSpacing,
  windowBorderRadius,
  mobileFullscreen,
  mobileBreakpointPx = 640,
  isLoading = false,
  defaultPreset = "desktop"
}: Props) {
  const [preset, setPreset] = useState<FramePreset>(defaultPreset);
  const frameRef = useRef<HTMLDivElement>(null);
  const [availableWidth, setAvailableWidth] = useState<number>(FRAME_PRESETS[defaultPreset].width);

  const target = FRAME_PRESETS[preset];

  useLayoutEffect(() => {
    const parent = frameRef.current?.parentElement;
    if (!parent) return;
    const measure = () => {
      const w = parent.clientWidth - 32;
      setAvailableWidth(Math.max(240, w));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(parent);
    return () => ro.disconnect();
  }, []);

  // Listen for chatDesignerSimulate events and apply a transient CSS class
  // so users can preview animations in this device frame.
  const widgetRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const onSimulate = (e: Event) => {
      const detail = (e as CustomEvent).detail as
        | { effect: string; variant?: string }
        | undefined;
      if (!detail || !widgetRef.current) return;
      const reduced =
        typeof window.matchMedia === "function" &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      const el = widgetRef.current;
      const toRemove: string[] = [];
      el.classList.forEach((c) => {
        if (c.startsWith("vel-sim-")) toRemove.push(c);
      });
      toRemove.forEach((c) => el.classList.remove(c));
      if (reduced) return;
      const cls = `vel-sim-${detail.effect}${detail.variant ? `-${detail.variant}` : ""}`;
      void el.offsetWidth;
      el.classList.add(cls);
      window.setTimeout(() => el.classList.remove(cls), 3000);
    };
    window.addEventListener("chatDesignerSimulate", onSimulate);
    return () => window.removeEventListener("chatDesignerSimulate", onSimulate);
  }, []);

  const frameWidth = Math.min(target.width, availableWidth);
  const frameHeight = Math.round((frameWidth / target.width) * target.height);

  // Scale factor if actual widget size exceeds frame
  const aw = actualWidth ?? 0;
  const ah = actualHeight ?? 0;
  const hasActual = aw > 0 && ah > 0;
  const widthScale = hasActual && aw > frameWidth - 32 ? (frameWidth - 32) / aw : 1;
  const heightScale = hasActual && ah > frameHeight - 80 ? (frameHeight - 80) / ah : 1;
  const scale = Math.min(1, widthScale, heightScale);
  const scalePct = Math.round(scale * 100);
  const verticalEdge =
    launcherVerticalPosition === "top"
      ? "top"
      : launcherVerticalPosition === "middle"
      ? "middle"
      : "bottom";
  const origin =
    `${verticalEdge === "top" ? "top" : verticalEdge === "middle" ? "center" : "bottom"} ${launcherPosition === "Left" ? "left" : "right"}`;
  const simulateMobileFill = (mobileFullscreen ?? true) && target.simulatedViewportWidth < mobileBreakpointPx;
  const radius = typeof windowBorderRadius === "number" ? windowBorderRadius : 12;

  const caption = simulateMobileFill
    ? `${target.label} preview · Fullscreen (viewport ${target.simulatedViewportWidth}px < ${mobileBreakpointPx}px breakpoint)`
    : hasActual
    ? scale < 1
      ? `Preview scaled to ${scalePct}% · Actual size: ${aw} × ${ah} px`
      : `Actual size: ${aw} × ${ah} px`
    : null;

  // Compute container positioning style honoring vertical anchor + mobile fullscreen
  const containerStyle: React.CSSProperties = simulateMobileFill
    ? {
        top: 32, // below browser chrome
        left: 0,
        right: 0,
        bottom: 0
      }
    : (() => {
        const base: React.CSSProperties = {
          [launcherPosition === "Left" ? "left" : "right"]: sideSpacing ?? 20,
          transform: scale < 1 ? `scale(${scale})` : undefined,
          transformOrigin: origin
        };
        if (verticalEdge === "top") {
          base.top = (bottomSpacing ?? 20) + 32; // below browser chrome
        } else if (verticalEdge === "middle") {
          base.top = "50%";
          base.transform = `${base.transform ?? ""} translateY(-50%)`.trim();
        } else {
          base.bottom = bottomSpacing ?? 20;
        }
        return base;
      })();

  // Inner widget host: honor configured height (workaround for remote module
  // not always reading windowHeight) and mask corners with the configured radius.
  const hostStyle: React.CSSProperties = simulateMobileFill
    ? {
        width: "100%",
        height: "100%",
        background: "transparent",
        overflow: "hidden",
        borderRadius: 0
      }
    : {
        position: "relative" as const,
        width: ah > 0 || aw > 0 ? aw || undefined : undefined,
        // Force the inner host to the configured height so the frame visibly
        // reflects the slider even when the remote messenger-widget ignores it.
        height: ah > 0 ? ah : undefined,
        background: "transparent",
        overflow: "visible",
        borderRadius: radius
      };

  return (
    <div className="flex flex-col items-center w-full py-4">
      <style>{SIMULATE_CSS}</style>
      {/* Simulated-preview caption */}
      <div className="text-xs text-slate-600 mb-2 flex items-center gap-1 px-2 text-center">
        <span aria-hidden="true">ⓘ</span>
        <span>Simulated preview — actual widget renders at configured size on your site.</span>
      </div>
      {/* Preset toolbar */}
      <div className="flex gap-2 mb-3" role="tablist" aria-label="Preview device preset">
        {(Object.keys(FRAME_PRESETS) as FramePreset[]).map((key) => {
          const isActive = key === preset;
          return (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => setPreset(key)}
              className={`px-3 py-1 text-xs font-medium rounded-md border transition-colors min-h-[28px] min-w-[48px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[var(--color-primary)] ${
                isActive
                  ? "text-white border-transparent"
                  : "bg-white text-slate-700 border-slate-300 hover:border-slate-400"
              }`}
              style={isActive ? { backgroundColor: "var(--color-primary)" } : undefined}
            >
              {FRAME_PRESETS[key].label}
            </button>
          );
        })}
      </div>

      {/* Device frame */}
      <div
        ref={frameRef}
        className="relative rounded-xl border border-slate-300 shadow-lg overflow-hidden"
        style={{
          width: frameWidth,
          height: frameHeight,
          background:
            "linear-gradient(180deg, #f8fafc 0%, #eef2f7 100%)"
        }}
      >
        {/* Browser chrome */}
        <div className="h-8 w-full bg-slate-100 border-b border-slate-200 flex items-center px-3 gap-2">
          <span className="w-2.5 h-2.5 rounded-full bg-red-400" />
          <span className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
          <span className="w-2.5 h-2.5 rounded-full bg-green-400" />
          <div className="flex-1 mx-3 h-4 bg-white rounded border border-slate-200 text-[10px] text-slate-400 px-2 flex items-center truncate">
            https://yoursite.com
          </div>
        </div>

        {/* Page mockup */}
        <div className="absolute left-0 right-0 top-8 bottom-0 pointer-events-none p-4 space-y-3">
          <div className="h-6 w-1/2 bg-slate-200 rounded" />
          <div className="h-3 w-full bg-slate-200/70 rounded" />
          <div className="h-3 w-5/6 bg-slate-200/70 rounded" />
          <div className="h-3 w-4/6 bg-slate-200/70 rounded" />
          <div className="h-20 w-full bg-slate-200/50 rounded mt-4" />
          <div className="h-3 w-full bg-slate-200/70 rounded" />
          <div className="h-3 w-3/6 bg-slate-200/70 rounded" />
        </div>

        {/* Widget container — respects configured offsets, vertical anchor, and mobile fill */}
        <div ref={widgetRef} className="absolute" style={containerStyle}>
          <div style={hostStyle}>{children}</div>
        </div>

        {/* Loading overlay — hides the widget's unstyled first-render flash */}
        {isLoading && (
          <div className="absolute inset-0 top-8 flex flex-col items-center justify-end pb-6 pointer-events-none"
               style={{ background: "transparent" }}>
            <div className="flex items-center gap-2 bg-white/80 backdrop-blur-sm border border-slate-200 rounded-full px-4 py-2 shadow text-xs text-slate-500">
              <span className="inline-block w-3 h-3 rounded-full border-2 border-slate-300 border-t-[var(--color-primary)] animate-spin" />
              Loading preview…
            </div>
          </div>
        )}
      </div>

      {/* Caption */}
      {caption && (
        <div
          className="mt-2 text-[11px] text-slate-600"
          role="status"
          aria-live="polite"
        >
          {caption}
        </div>
      )}
    </div>
  );
}
